import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, RegisterPayload } from '../types';
import { AuthAPI, clearAuthTokens, setOnSessionExpired } from '../api';
import { STORAGE_KEYS, USER_ROLES } from '../constants';
import { createZustandStorage } from '../utils/storage';
import {
  hasRole,
  isOnlyRole,
  hasAtLeastRole,
  getHighestRole,
  canAccessAdmin,
  canAccessManager,
  isCustomer,
  isStaffMember,
} from '../utils/roles';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  // Messaggio informativo (es. accesso limitato)
  accessMessage: string | null;
  isUserRole: boolean;
  isManagerRole: boolean;
  isAdminRole: boolean;
  hasManagerRole: boolean;
  hasAdminRole: boolean;
  highestRole: string | null;
  // Indica se l'utente ha accesso completo (Customer associato o ruolo Admin/Manager)
  hasFullAccess: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<{ message: string }>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  updateUser: (user: User) => void;
  clearError: () => void;
  initialize: () => Promise<void>;
  handleSessionExpired: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      setOnSessionExpired(() => {
        get().handleSessionExpired();
      });

      return {
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
        accessMessage: null,

        get isUserRole() {
          return isOnlyRole(get().user?.roles, USER_ROLES.USER);
        },
        get isManagerRole() {
          return isOnlyRole(get().user?.roles, USER_ROLES.MANAGER);
        },
        get isAdminRole() {
          return isOnlyRole(get().user?.roles, USER_ROLES.ADMIN);
        },

        get hasManagerRole() {
          return hasAtLeastRole(get().user?.roles, USER_ROLES.MANAGER);
        },
        get hasAdminRole() {
          return hasAtLeastRole(get().user?.roles, USER_ROLES.ADMIN);
        },

        get highestRole() {
          return getHighestRole(get().user?.roles);
        },

        get hasFullAccess() {
          const user = get().user;
          if (!user) return false;
          // Admin e Manager hanno sempre accesso completo
          if (hasAtLeastRole(user.roles, USER_ROLES.MANAGER)) return true;
          // Per Customer/User: usa accessLevel se presente, altrimenti fallback su customer associato
          if (user.accessLevel) return user.accessLevel === 'full';
          return !!user.customer;
        },

        login: async (email: string, password: string) => {
          set({ isLoading: true, error: null, accessMessage: null });

          try {
            const response = await AuthAPI.login(email, password);

            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              // Cattura il messaggio informativo (es. accesso limitato)
              accessMessage: response.message || null,
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Errore durante il login';
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: message,
              accessMessage: null,
            });
            throw error;
          }
        },

        register: async (payload: RegisterPayload) => {
          set({ isLoading: true, error: null });

          try {
            const response = await AuthAPI.register(payload);
            set({ isLoading: false });
            return response;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Errore durante la registrazione';
            set({ isLoading: false, error: message });
            throw error;
          }
        },

        logout: async () => {
          set({ isLoading: true });

          try {
            await AuthAPI.logout();
          } catch {
            // Ignore logout errors
          } finally {
            await clearAuthTokens();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              accessMessage: null,
            });
          }
        },

        loadUser: async () => {
          set({ isLoading: true });

          try {
            const user = await AuthAPI.getCurrentUser();
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        },

        updateUser: (user: User) => {
          set({ user });
        },

        clearError: () => {
          set({ error: null });
        },

        handleSessionExpired: () => {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: 'Sessione scaduta. Effettua nuovamente il login.',
          });
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },

        initialize: async () => {
          const state = get();
          if (state.isAuthenticated && state.user) {
            try {
              await get().loadUser();
            } catch {
              // Ignore errors
            }
          }
        },
      };
    },
    {
      name: STORAGE_KEYS.USER_DATA,
      storage: createJSONStorage(() => createZustandStorage()),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export const selectUser = (state: AuthStore) => state.user;
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;
export const selectIsLoading = (state: AuthStore) => state.isLoading;
export const selectAuthError = (state: AuthStore) => state.error;

export const selectIsUserRole = (state: AuthStore) => isOnlyRole(state.user?.roles, USER_ROLES.USER);
export const selectIsManagerRole = (state: AuthStore) => isOnlyRole(state.user?.roles, USER_ROLES.MANAGER);
export const selectIsAdminRole = (state: AuthStore) => isOnlyRole(state.user?.roles, USER_ROLES.ADMIN);

export const selectHasManagerRole = (state: AuthStore) =>
  hasAtLeastRole(state.user?.roles, USER_ROLES.MANAGER);
export const selectHasAdminRole = (state: AuthStore) =>
  hasAtLeastRole(state.user?.roles, USER_ROLES.ADMIN);

export const selectHighestRole = (state: AuthStore) => getHighestRole(state.user?.roles);
export const selectCanAccessAdmin = (state: AuthStore) => canAccessAdmin(state.user?.roles);
export const selectCanAccessManager = (state: AuthStore) => canAccessManager(state.user?.roles);

export const selectUserRoles = (state: AuthStore) => state.user?.roles || [];

export const selectHasRole = (roleName: string) => (state: AuthStore) =>
  hasRole(state.user?.roles, roleName);

export const selectHasAtLeastRole = (minimumRole: string) => (state: AuthStore) =>
  hasAtLeastRole(state.user?.roles, minimumRole);

export const selectIsCustomer = (state: AuthStore) => isCustomer(state.user?.roles);
export const selectIsStaffMember = (state: AuthStore) => isStaffMember(state.user?.roles);

// Selectors per accesso limitato
export const selectAccessMessage = (state: AuthStore) => state.accessMessage;
export const selectHasFullAccess = (state: AuthStore) => {
  const user = state.user;
  if (!user) return false;
  // Admin e Manager hanno sempre accesso completo
  if (hasAtLeastRole(user.roles, USER_ROLES.MANAGER)) return true;
  // Per Customer/User: usa accessLevel se presente, altrimenti fallback su customer associato
  if (user.accessLevel) return user.accessLevel === 'full';
  return !!user.customer;
};
export const selectHasLimitedAccess = (state: AuthStore) => {
  const user = state.user;
  if (!user) return false;
  if (user.accessLevel) return user.accessLevel === 'limited';
  return !user.customer;
};
export const selectHasCustomer = (state: AuthStore) =>
  state.user?.hasCustomer ?? !!state.user?.customer;

// Tipo account (Privato/Azienda). Usato per nascondere pagine specifiche
// (Cantina, Statistiche Prodotti, Statistiche Vendite) ai Privati anche
// se via URL diretto.
export const selectAccountType = (state: AuthStore) => state.user?.accountType;
export const selectIsPrivato = (state: AuthStore) =>
  state.user?.accountType === 'Privato';
export const selectIsAzienda = (state: AuthStore) =>
  state.user?.accountType === 'Azienda';

/**
 * Selettore unico per accesso a Cantina e Statistiche.
 * Regola:
 *  - Staff (Manager/Admin) ha sempre accesso.
 *  - Privato non ha mai accesso, neanche se ha un Customer associato.
 *  - Azienda: serve hasCustomer = true (logica preesistente).
 */
export const selectCanAccessWineryArea = (state: AuthStore) => {
  const user = state.user;
  if (!user) return false;
  if (hasAtLeastRole(user.roles, USER_ROLES.MANAGER)) return true;
  if (user.accountType === 'Privato') return false;
  return user.hasCustomer ?? !!user.customer;
};
